import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Facebook, Send, MessageCircle, CheckCircle } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import KennelSEO from "./KennelSEO";
import PageHero from "./PageHero";

const WA = "https://wa.me/48663746095";

const KennelContact = () => {
  const [form, setForm] = useState({ name: "", message: "", contact: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    const text =
      `Dzień dobry! Mam pytanie do hodowli Wolfe vom Katzenberg.\n\n` +
      `Imię: ${form.name || "—"}\n` +
      `Kontakt: ${form.contact || "—"}\n` +
      `Wiadomość: ${form.message || "—"}`;
    window.open(`${WA}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
    setSent(true);
    setSending(false);
  };

  return (
    <div>
      <KennelSEO
        title="Kontakt | Wolfe vom Katzenberg — Hodowla Owczarka Niemieckiego"
        description="Skontaktuj sie z hodowla Wolfe vom Katzenberg. Zapytaj o szczenieta owczarka niemieckiego, aktualne mioty i rezerwacje."
        canonical="https://wolfevomkatzenberg.pl/kontakt"
      />

      <PageHero
        breadcrumbs={[{ label: "KONTAKT" }]}
        label="KONTAKT"
        title="Masz pytanie o szczenię lub naszą hodowlę?"
        description="Chętnie odpowiemy na pytania i pomożemy Ci wybrać odpowiedniego psa. Najszybciej skontaktujesz się z nami przez WhatsApp."
        testId="contact"
      />

      {/* ── MAIN CONTENT ── */}
      <div className="px-6 md:px-12 py-14 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-[1fr_340px] gap-12 lg:gap-16">

          {/* ── LEFT COLUMN ── */}
          <div>
            {/* WhatsApp CTA */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <a href="https://wa.me/48663746095" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#25D366] text-white px-8 py-4 text-sm font-medium hover:bg-[#20BA5A] transition-colors"
                data-testid="whatsapp-main-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Napisz na WhatsApp
              </a>

              <div className="flex flex-col gap-2.5 mt-6 ml-1">
                {["szybki kontakt", "pomoc w wyborze szczenięcia", "odpowiedź bez zbędnego czekania"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <CheckCircle className="w-3.5 h-3.5 text-[#25D366]" />
                    <span className="text-[#B8B8B8]/60 text-xs">{t}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Divider */}
            <div className="my-10 border-t border-[#2A2A2D]" />

            {/* Form */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              {sent ? (
                <div className="bg-[#1A1A1D] border border-[#2A2A2D] p-12 text-center" data-testid="contact-success">
                  <MessageCircle className="w-8 h-8 text-[#25D366] mx-auto mb-4" />
                  <h3 className="font-serif text-xl text-[#F5F5F5] mb-3">Otwieramy WhatsApp…</h3>
                  <p className="text-[#B8B8B8]/50 text-sm">Twoja wiadomość została przygotowana — wystarczy ją wysłać na WhatsApp. Jeśli okno się nie otworzyło, kliknij „Napisz na WhatsApp" powyżej.</p>
                </div>
              ) : (
                <>
                  <h2 className="font-serif text-lg text-[#F5F5F5] mb-6" data-testid="form-heading">
                    Lub wyślij do nas wiadomość
                  </h2>
                  <form onSubmit={handleSubmit} className="bg-[#1A1A1D] border border-[#2A2A2D] p-6 md:p-8 space-y-5" data-testid="contact-form">
                    <div>
                      <label className="text-[10px] tracking-[0.15em] text-[#B8B8B8]/40 mb-2 block uppercase">Imię</label>
                      <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                        className="w-full bg-[#141416] border border-[#2A2A2D] px-4 py-3 text-sm text-[#F5F5F5] focus:border-[#D4AF37] focus:outline-none transition-colors"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.15em] text-[#B8B8B8]/40 mb-2 block uppercase">Wiadomość</label>
                      <textarea required rows="5" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                        placeholder="Napisz, w czym możemy pomóc"
                        className="w-full bg-[#141416] border border-[#2A2A2D] px-4 py-3 text-sm text-[#F5F5F5] placeholder:text-[#B8B8B8]/20 focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"
                        data-testid="contact-message"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.15em] text-[#B8B8B8]/40 mb-2 block uppercase">Telefon lub e-mail <span className="text-[#B8B8B8]/20">(opcjonalnie)</span></label>
                      <input type="text" value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })}
                        className="w-full bg-[#141416] border border-[#2A2A2D] px-4 py-3 text-sm text-[#F5F5F5] focus:border-[#D4AF37] focus:outline-none transition-colors"
                        data-testid="contact-info"
                      />
                    </div>
                    <button type="submit" disabled={sending} data-testid="contact-submit"
                      className="w-full py-3.5 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-[0.2em] hover:bg-[#C8A96A] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {sending ? "WYSYŁANIE..." : <><Send className="w-4 h-4" /> WYŚLIJ WIADOMOŚĆ</>}
                    </button>
                    <p className="text-[10px] text-[#B8B8B8]/30 text-center tracking-wider">Odpowiadamy tak szybko, jak to możliwe.</p>
                  </form>
                </>
              )}
            </motion.div>
          </div>

          {/* ── RIGHT COLUMN ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="space-y-6">
            {/* Contact info */}
            <div className="bg-[#1A1A1D] border border-[#2A2A2D] p-7" data-testid="contact-details">
              <h3 className="font-serif text-lg text-[#F5F5F5] mb-6">Dane kontaktowe</h3>
              <div className="space-y-5 text-sm text-[#B8B8B8]">
                <a href="tel:+48663746095" className="flex items-center gap-3 hover:text-[#D4AF37] transition-colors" data-testid="contact-phone-link">
                  <Phone className="w-4 h-4 text-[#C8A96A]" /> +48 663 746 095
                </a>
                <a href="mailto:katzenberg.hodowla@gmail.com" className="flex items-center gap-3 hover:text-[#D4AF37] transition-colors" data-testid="contact-email-link">
                  <Mail className="w-4 h-4 text-[#C8A96A]" /> katzenberg.hodowla@gmail.com
                </a>
                <a href="https://wa.me/48663746095" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-[#D4AF37] transition-colors" data-testid="contact-wa-link">
                  <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp
                </a>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#C8A96A] mt-0.5 shrink-0" />
                  <span>Kałowice 89<br />55-106 Zawonia</span>
                </div>
                <a href="https://www.facebook.com/profile.php?id=61593187978675" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-[#D4AF37] transition-colors">
                  <Facebook className="w-4 h-4 text-[#C8A96A]" /> Facebook
                </a>
              </div>
            </div>

            {/* QR Code — WhatsApp */}
            <div className="bg-[#1A1A1D] border border-[#2A2A2D] p-7" data-testid="qr-code-section">
              <p className="text-xs text-[#B8B8B8]/50 mb-5 leading-relaxed">
                Zeskanuj kod i napisz do nas bezpośrednio na WhatsApp
              </p>
              <div className="flex justify-center">
                <div className="bg-white p-3 inline-block">
                  <QRCodeSVG
                    value="https://wa.me/48663746095"
                    size={140}
                    bgColor="#ffffff"
                    fgColor="#0B0B0D"
                    level="M"
                    includeMargin={false}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ═══ MAP ═══ */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-12">
          <div className="bg-[#1A1A1D] border border-[#2A2A2D] p-2 overflow-hidden">
            <iframe
              title="Lokalizacja hodowli Wolfe vom Katzenberg"
              src="https://maps.google.com/maps?q=Ka%C5%82owice%2089%2C%2055-106%20Zawonia&z=14&hl=pl&output=embed"
              width="100%" height="350"
              style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) brightness(0.9) contrast(1.1)" }}
              allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </div>
          <p className="text-[10px] text-[#B8B8B8]/40 text-center mt-3 tracking-wider">KAŁOWICE 89, 55-106 ZAWONIA</p>
        </motion.div>
      </div>
    </div>
  );
};
export default KennelContact;
